/**
 * Which kind of screen is this, really.
 *
 * The eighteen-screen surface has to land on a watch face, a phone, a tablet,
 * a laptop, a buggy head unit and a lounge-room TV without eighteen builds. It
 * is one web route, so the only honest signal is the viewport itself — a class
 * on <html> that the stylesheet keys off. No user agent sniffing: a head unit
 * lies about its UA, a 240 px watch window does not lie about being 240 px.
 *
 * Order matters. Aspect ratio decides car-vs-tablet, because a head unit is a
 * short wide box and a tablet is not.
 */
export type SurfaceKind = 'watch' | 'phone' | 'car' | 'tablet' | 'desktop' | 'tv';

export function surfaceKind(w = window.innerWidth, h = window.innerHeight): SurfaceKind {
  const min = Math.min(w, h);
  const ratio = w / Math.max(h, 1);
  if (min <= 320 && w <= 460) return 'watch';
  if (w >= 1600 && ratio > 1.5) return 'tv';
  if (ratio >= 1.9 && h <= 620) return 'car';
  if (w < 700) return 'phone';
  if (w < 1200) return 'tablet';
  return 'desktop';
}

/** Stamp the class and keep it current across rotation and window resize. */
export function watchSurface(onChange?: (k: SurfaceKind) => void): () => void {
  const apply = () => {
    const k = surfaceKind();
    if (document.documentElement.dataset.surface !== k) {
      document.documentElement.dataset.surface = k;
      onChange?.(k);
    }
  };
  apply();
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  return () => {
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
  };
}
